<template>
    <v-container fluid class="history-page pa-6">

        <div class="filter-row mb-5">
            <v-chip
                v-for="cat in categories"
                :key="cat.value"
                class="filter-chip mr-2 mb-2"
                label
                :color="selected_categories.includes(cat.value) ? cat.color : '#F5F5F5'"
                :text-color="selected_categories.includes(cat.value) ? 'white' : '#525A68'"
                @click="toggleCategory(cat.value)"
            >
                <v-icon left x-small>{{ cat.icon }}</v-icon>
                {{ cat.label }}
            </v-chip>
            <v-btn v-if="selected_categories.length" text x-small color="primary" class="mb-2" @click="clearFilters()">
                Clear filters
            </v-btn>
        </div>

        <!-- Loading state -->
        <div v-if="loading && groups.length === 0" class="history-empty-state">
            <v-progress-circular indeterminate color="primary" size="40" class="mb-3" />
            <div class="grey--text text--darken-1 body-2">Loading history...</div>
        </div>

        <!-- Empty state -->
        <div v-else-if="groups.length === 0" class="history-empty-state">
            <v-icon size="60" color="#E0E0E0" class="mb-3">mdi-history</v-icon>
            <div class="grey--text text--darken-1 body-2">No history recorded yet</div>
        </div>

        <!-- History list -->
        <div v-else>
            <v-card v-for="group in groups" :key="group.batch_id" class="history-card mb-3" elevation="0">
                <v-card-text class="pa-4">
                    <div class="history-card-header">
                        <div class="d-flex align-center flex-wrap" style="gap:6px;">
                            <v-chip
                                v-for="cat in group.categories"
                                :key="cat"
                                x-small label
                                :color="categoryMeta(cat).color"
                                text-color="white"
                            >
                                <v-icon left x-small>{{ categoryMeta(cat).icon }}</v-icon>
                                {{ categoryMeta(cat).label }}
                            </v-chip>
                            <v-chip x-small label :color="actionMeta(group.action).color" text-color="white">
                                {{ actionMeta(group.action).label }}
                            </v-chip>
                        </div>
                        <div class="history-meta grey--text">
                            {{ formatDate(group.created_at) }} &middot; {{ group.user ? group.user.name : 'System' }}
                        </div>
                    </div>

                    <div class="history-title mt-2">
                        {{ actionMeta(group.action).verb }}
                        <strong>{{ group.model_label || 'record' }}</strong>
                    </div>

                    <div v-if="group.fields.length" class="history-fields mt-2">
                        <div v-for="(field, idx) in group.fields" :key="idx" class="history-field-row">
                            <span class="field-name">{{ fieldLabel(field.field_name) }}:</span>
                            <span class="old-value">{{ formatValue(field.old_value, field.field_name) }}</span>
                            <v-icon x-small class="mx-1" color="#9E9E9E">mdi-arrow-right</v-icon>
                            <span class="new-value">{{ formatValue(field.new_value, field.field_name) }}</span>
                        </div>
                    </div>

                    <div v-if="group.email" class="history-fields mt-2">
                        <div class="history-field-row">
                            <span class="field-name">Subject:</span>
                            <span class="new-value">{{ group.email.subject || '—' }}</span>
                        </div>
                        <div class="history-field-row">
                            <span class="field-name">To:</span>
                            <span class="new-value">{{ formatRecipient(group.email.to_name, group.email.to_email) }}</span>
                        </div>
                        <div class="history-field-row">
                            <span class="field-name">From:</span>
                            <span class="new-value">{{ formatRecipient(group.email.from_name, group.email.from_email) }}</span>
                        </div>
                        <div v-if="group.email.cc" class="history-field-row">
                            <span class="field-name">Cc:</span>
                            <span class="new-value">{{ group.email.cc }}</span>
                        </div>
                        <div v-if="group.email.bcc" class="history-field-row">
                            <span class="field-name">Bcc:</span>
                            <span class="new-value">{{ group.email.bcc }}</span>
                        </div>

                        <v-btn
                            v-if="group.email.body"
                            text x-small color="primary"
                            class="mt-1 pl-0"
                            @click="toggleEmailBody(group.batch_id)"
                        >
                            {{ expanded_emails.includes(group.batch_id) ? 'Hide email' : 'View email' }}
                        </v-btn>
                        <div
                            v-if="group.email.body && expanded_emails.includes(group.batch_id)"
                            class="email-body-preview mt-2"
                            v-html="group.email.body"
                        ></div>
                    </div>
                </v-card-text>
            </v-card>

            <div v-if="page < last_page" class="text-center mt-4">
                <v-btn text :loading="loading" @click="loadMore()">Load more</v-btn>
            </div>
        </div>

    </v-container>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    data: () => ({
        loading: true,
        history: [],
        page: 1,
        last_page: 1,
        per_page: 25,
        selected_categories: [],
        categories: [
            { value: 'invitee', label: 'Invitees', color: '#3988AA', icon: 'mdi-account-group-outline' },
            { value: 'setting', label: 'Settings', color: '#7C7C7C', icon: 'mdi-cog-outline' },
            { value: 'quote', label: 'Quotes', color: '#FAAF3A', icon: 'mdi-file-document-outline' },
            { value: 'supplier', label: 'Suppliers', color: '#2E7D32', icon: 'mdi-store-outline' },
            { value: 'itinerary', label: 'Itinerary', color: '#8E24AA', icon: 'mdi-map-marker-outline' },
            { value: 'document', label: 'Documents', color: '#1565C0', icon: 'mdi-file-outline' },
            { value: 'email', label: 'Emails', color: '#5E35B1', icon: 'mdi-email-outline' },
            { value: 'checklist', label: 'Checklist', color: '#00897B', icon: 'mdi-format-list-checks' },
        ],
        expanded_emails: [],
        booking_statuses: [
            { id: 1, status: 'Unconfirmed' },
            { id: 2, status: 'Requested' },
            { id: 3, status: 'Confirmed' },
            { id: 4, status: 'Cancelled' },
            { id: 5, status: 'Cancellation Pending' },
        ],
        payment_statuses: [
            { id: 1, status: 'Notification Not Sent' },
            { id: 2, status: 'Notification Sent' },
            { id: 3, status: 'Payment Received' },
            { id: 4, status: 'Payment Not Received' },
        ],
    }),
    async mounted() {
        await this.fetchHistory(1)
    },
    computed: {
        ...mapGetters({
            event: 'eventsStore/getSelectedEventGetter',
        }),
        groups() {
            const map = new Map()
            const order = []
            this.history.forEach(row => {
                if (!map.has(row.batch_id)) {
                    map.set(row.batch_id, {
                        batch_id: row.batch_id,
                        action: row.action,
                        model_label: row.model_label,
                        user: row.user,
                        created_at: row.created_at,
                        categories: [],
                        fields: [],
                        email: row.email || null,
                    })
                    order.push(row.batch_id)
                }
                const group = map.get(row.batch_id)
                if (!group.categories.includes(row.category)) {
                    group.categories.push(row.category)
                }
                if (row.action === 'updated') {
                    group.fields.push(row)
                }
            })
            return order.map(id => map.get(id))
        },
    },
    methods: {
        categoryMeta(value) {
            return this.categories.find(c => c.value === value) || { label: value, color: '#9E9E9E', icon: 'mdi-circle-outline' }
        },
        actionMeta(action) {
            const meta = {
                created: { label: 'Created', verb: 'Created', color: '#2E7D32' },
                updated: { label: 'Updated', verb: 'Updated', color: '#1565C0' },
                deleted: { label: 'Deleted', verb: 'Deleted', color: '#C62828' },
                sent: { label: 'Sent', verb: 'Sent email to', color: '#5E35B1' },
            }
            return meta[action] || { label: action, verb: action, color: '#9E9E9E' }
        },
        toggleCategory(value) {
            const idx = this.selected_categories.indexOf(value)
            if (idx === -1) {
                this.selected_categories.push(value)
            } else {
                this.selected_categories.splice(idx, 1)
            }
            this.fetchHistory(1)
        },
        clearFilters() {
            this.selected_categories = []
            this.fetchHistory(1)
        },
        fieldLabel(field) {
            if (!field) return ''
            if (field === 'rsvp_status') return 'RSVP'
            if (field === 'stag_hen') return 'Stag/Hen'
            return field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        },
        formatValue(value, field_name) {
            if (value === null || value === undefined || value === '') return '—'
            if (field_name === 'booking_status_id') {
                return this.booking_statuses.find(s => s.id === Number(value))?.status || value
            }
            if (field_name === 'rsvp_status') {
                return { declined: 'Not coming', attending: 'Coming' }[value] || value
            }
            if (field_name === 'payment_status_id') {
                return this.payment_statuses.find(s => s.id === Number(value))?.status || value
            }
            return value
        },
        formatDate(date) {
            return this.$date(date).format('D MMM YYYY, h:mm A')
        },
        formatRecipient(name, email) {
            if (!email) return '—'
            return name ? `${name} <${email}>` : email
        },
        toggleEmailBody(batch_id) {
            const idx = this.expanded_emails.indexOf(batch_id)
            if (idx === -1) {
                this.expanded_emails.push(batch_id)
            } else {
                this.expanded_emails.splice(idx, 1)
            }
        },
        async fetchHistory(page) {
            this.loading = true
            try {
                const params = { page, per_page: this.per_page }
                if (this.selected_categories.length) {
                    params.categories = this.selected_categories.join(',')
                }
                const { data } = await this.$axios.get(`admin/events/get_event_history/${this.event.id}`, { params })
                if (data.response) {
                    this.history = page === 1 ? data.data : this.history.concat(data.data)
                    this.page = data.current_page
                    this.last_page = data.last_page
                }
            } finally {
                this.loading = false
            }
        },
        loadMore() {
            if (this.page < this.last_page) {
                this.fetchHistory(this.page + 1)
            }
        },
    },
}
</script>

<style scoped lang="scss">
.history-page {
    background-color: #F5F6FA;
    min-height: 100%;
}

.filter-chip {
    cursor: pointer;
    font-weight: 600;
    font-size: 12px;
}

.history-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    text-align: center;
}

.history-card {
    border-radius: 12px !important;
    border: 1px solid #E8E8E8 !important;
    background-color: #fff;
}

.history-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}

.history-meta {
    font-size: 11px;
    white-space: nowrap;
}

.history-title {
    font-size: 14px;
    color: #2C2C2C;
}

.history-fields {
    border-top: 1px solid #F0F0F0;
    padding-top: 8px;
}

.history-field-row {
    font-size: 12px;
    color: #525A68;
    padding: 3px 0;
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
}

.field-name {
    font-weight: 700;
    color: #2C2C2C;
    margin-right: 6px;
}

.old-value,
.new-value {
    white-space: pre-line;
}

.old-value {
    color: #C62828;
    text-decoration: line-through;
}

.new-value {
    color: #2E7D32;
    font-weight: 600;
}

.email-body-preview {
    border: 1px solid #F0F0F0;
    border-radius: 8px;
    padding: 12px;
    background-color: #FAFAFA;
    font-size: 13px;
    color: #2C2C2C;
    max-height: 400px;
    overflow-y: auto;
}
</style>
